/**
 * Lightweight markdown → typed blocks for PDF / DOCX / print.
 * Handles headings, paragraphs, lists, hr, fenced code, and **bold** / *italic* / `code`.
 */

export type TextRun = {
  text: string
  bold?: boolean
  italic?: boolean
  code?: boolean
}

export type DocBlock =
  | { type: 'heading'; level: 1 | 2 | 3; runs: TextRun[] }
  | { type: 'paragraph'; runs: TextRun[] }
  | { type: 'listItem'; ordered: boolean; index: number; runs: TextRun[] }
  | { type: 'code'; text: string }
  | { type: 'hr' }

// --------------

/** Strip .md / .pdf / etc so titles don’t show “Cover_Letter.md” in the PDF. */
export function displayDocumentTitle(raw: string | null | undefined): string {
  const trimmed = (raw || '').trim()
  if (!trimmed) return 'Document'
  const stripped = trimmed
    .replace(/(?:\.(?:md|markdown|txt|rtf|pdf|docx?|odt|html?|csv))+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return stripped || 'Document'
}

// --------------

function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

/**
 * Parse a single line of inline markdown into styled runs.
 * Order: code → bold → italic (greedy enough for letters/cover letters).
 */
export function parseInlineRuns(raw: string): TextRun[] {
  const text = decodeEntities(raw)
  if (!text) return []

  const runs: TextRun[] = []
  // Split on `code`, **bold**, *italic* / _italic_
  const token =
    /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = token.exec(text)) !== null) {
    if (match.index > last) {
      runs.push({ text: text.slice(last, match.index) })
    }
    const tok = match[0]
    if (tok.startsWith('`')) {
      runs.push({ text: tok.slice(1, -1), code: true })
    } else if (tok.startsWith('**') || tok.startsWith('__')) {
      runs.push({ text: tok.slice(2, -2), bold: true })
    } else {
      runs.push({ text: tok.slice(1, -1), italic: true })
    }
    last = match.index + tok.length
  }
  if (last < text.length) {
    runs.push({ text: text.slice(last) })
  }
  return runs.filter(run => run.text.length > 0)
}

// --------------

function pushParagraph(blocks: DocBlock[], line: string) {
  const runs = parseInlineRuns(line)
  if (runs.length === 0) return
  blocks.push({ type: 'paragraph', runs })
}

/**
 * Convert markdown source into document blocks.
 */
export function markdownToBlocks(markdown: string): DocBlock[] {
  const source = (markdown || '').replace(/\r\n/g, '\n')
  const blocks: DocBlock[] = []
  const lines = source.split('\n')
  let i = 0
  let orderedIndex = 0

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code
    if (/^```/.test(line)) {
      i += 1
      const codeLines: string[] = []
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i])
        i += 1
      }
      if (i < lines.length) i += 1 // closing fence
      blocks.push({ type: 'code', text: codeLines.join('\n') })
      orderedIndex = 0
      continue
    }

    // Blank
    if (!line.trim()) {
      orderedIndex = 0
      i += 1
      continue
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) {
      blocks.push({ type: 'hr' })
      orderedIndex = 0
      i += 1
      continue
    }

    // Headings
    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading) {
      const level = Math.min(heading[1].length, 3) as 1 | 2 | 3
      blocks.push({
        type: 'heading',
        level,
        runs: parseInlineRuns(heading[2].trim())
      })
      orderedIndex = 0
      i += 1
      continue
    }

    // Unordered list
    const ul = /^[-*+]\s+(.*)$/.exec(line)
    if (ul) {
      blocks.push({
        type: 'listItem',
        ordered: false,
        index: 0,
        runs: parseInlineRuns(ul[1])
      })
      orderedIndex = 0
      i += 1
      continue
    }

    // Ordered list
    const ol = /^(\d+)\.\s+(.*)$/.exec(line)
    if (ol) {
      orderedIndex = Number(ol[1]) || orderedIndex + 1
      blocks.push({
        type: 'listItem',
        ordered: true,
        index: orderedIndex,
        runs: parseInlineRuns(ol[2])
      })
      i += 1
      continue
    }

    // One source line → one paragraph. Soft-joining mashed cover-letter
    // address blocks ("Hiring Team" / company / city) into a single line.
    pushParagraph(blocks, line)
    orderedIndex = 0
    i += 1
  }

  return blocks
}

// --------------

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function runsToHtml(runs: TextRun[]): string {
  return runs
    .map(run => {
      let html = escapeHtml(run.text)
      if (run.code) html = `<code>${html}</code>`
      if (run.bold) html = `<strong>${html}</strong>`
      if (run.italic) html = `<em>${html}</em>`
      return html
    })
    .join('')
}

/** Branded HTML for browser print — real headings/bold, not a monospace dump. */
export function markdownToPrintHtml(title: string, markdown: string): string {
  const safeTitle = escapeHtml(displayDocumentTitle(title))
  const blocks = markdownToBlocks(markdown)
  const body = blocks
    .map(block => {
      switch (block.type) {
        case 'heading': {
          const tag = `h${block.level}`
          return `<${tag}>${runsToHtml(block.runs)}</${tag}>`
        }
        case 'paragraph':
          return `<p>${runsToHtml(block.runs)}</p>`
        case 'listItem': {
          const marker = block.ordered ? `${block.index}. ` : '• '
          return `<p class="li">${escapeHtml(marker)}${runsToHtml(block.runs)}</p>`
        }
        case 'code':
          return `<pre><code>${escapeHtml(block.text)}</code></pre>`
        case 'hr':
          return `<hr />`
        default:
          return ''
      }
    })
    .join('\n')

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>
    @page { margin: 0.75in; }
    body {
      font-family: "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif;
      font-size: 12pt;
      line-height: 1.45;
      color: #111;
      margin: 0;
      padding: 24px;
    }
    h1 { font-size: 20pt; margin: 0 0 14px; font-weight: 700; }
    h2 { font-size: 14pt; margin: 18px 0 8px; font-weight: 700; }
    h3 { font-size: 12.5pt; margin: 14px 0 6px; font-weight: 700; }
    p { margin: 0 0 10px; }
    p.li { margin: 0 0 4px; padding-left: 12px; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    code, pre {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 10.5pt;
    }
    pre {
      background: #f4f4f5;
      padding: 10px 12px;
      border-radius: 6px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    hr {
      border: 0;
      border-top: 1px solid #d4d4d8;
      margin: 16px 0;
    }
  </style>
</head>
<body>
  ${body || `<p>${safeTitle}</p>`}
</body>
</html>`
}
